import { useEffect, useMemo, useState } from "react";
import {
  buildTransformedUrl,
  safeHttpUrl,
  type TransformParams,
} from "@/lib/images";
import type { MediaItem, MediaSubmission } from "@adva/types";

// Pure logic + the image-fallback hook shared by the media components. Kept in
// a non-component module so the component files satisfy react-refresh's
// only-export-components rule and the grouping/caption logic stays unit-
// testable without rendering.

/**
 * Caption precedence (§I4.4): the operator-authored `caption` is the verbatim
 * contract; `ai_caption` is a gap-filler used ONLY when generation completed
 * (`ai_caption_status === "done"`), never an override. Returns `null` when
 * neither is present.
 */
export function photoCaption(item: MediaItem): string | null {
  if (item.caption && item.caption.trim()) return item.caption;
  if (item.ai_caption_status === "done" && item.ai_caption?.trim()) {
    return item.ai_caption;
  }
  return null;
}

/**
 * The §I7 fallback chain as an ordered list of DISTINCT loadable sources:
 * (1) transformed CDN URL → (2) raw `public_url`. When the list is exhausted
 * `src` is `null` and the caller renders the §I7.1 step-3 glyph — never the
 * browser broken-image glyph (§I7.2). De-duped so a pass-through host (where
 * transformed === raw) doesn't retry the same failing URL twice.
 */
export function useImageFallback(
  publicUrl: string | null | undefined,
  slot: TransformParams,
): { src: string | null; onError: () => void } {
  const sources = useMemo(() => {
    const transformed = buildTransformedUrl(publicUrl, slot);
    const raw = safeHttpUrl(publicUrl);
    return [transformed, raw].filter(
      (u, i, arr): u is string => !!u && arr.indexOf(u) === i,
    );
  }, [publicUrl, slot]);

  const [step, setStep] = useState(0);
  // Reset when the source set changes (e.g. the lightbox swaps the active photo
  // into the same cell instance).
  useEffect(() => setStep(0), [sources]);

  return {
    src: sources[step] ?? null,
    onError: () => setStep((s) => s + 1),
  };
}

// ── Stage grouping (§1 surface deliverable) ──

// `issue`/`general` are real enum values that simply don't render when empty;
// `null` (and any unknown value) falls to the muted "Untagged" bucket.
const KNOWN_STAGES = new Set<NonNullable<MediaItem["photo_stage"]>>([
  "before",
  "during",
  "after",
  "issue",
  "general",
]);

const STAGE_ORDER: { stage: MediaItem["photo_stage"]; label: string }[] = [
  { stage: "before", label: "Before" },
  { stage: "during", label: "During" },
  { stage: "after", label: "After" },
  { stage: "issue", label: "Issue" },
  { stage: "general", label: "General" },
  { stage: null, label: "Untagged" },
];

interface GallerySection {
  stage: MediaItem["photo_stage"];
  label: string;
  photos: MediaItem[];
  /** Index of this section's first photo in the flattened, ordered array. */
  startIndex: number;
}

function stageKey(stage: MediaItem["photo_stage"]): MediaItem["photo_stage"] {
  return stage && KNOWN_STAGES.has(stage) ? stage : null;
}

/**
 * Group image items into ordered, non-empty stage sections (Before → During →
 * After → Issue → General → Untagged). Within a section, `display_order` asc
 * with `created_at` as the tiebreak. Returns the sections (each carrying its
 * `startIndex`) plus the flattened ordered array the lightbox indexes into.
 */
export function groupPhotosByStage(items: MediaItem[]): {
  sections: GallerySection[];
  flat: MediaItem[];
} {
  const byStage = new Map<MediaItem["photo_stage"], MediaItem[]>();
  for (const item of items) {
    const key = stageKey(item.photo_stage);
    const bucket = byStage.get(key) ?? [];
    bucket.push(item);
    byStage.set(key, bucket);
  }

  const sections: GallerySection[] = [];
  let startIndex = 0;
  for (const { stage, label } of STAGE_ORDER) {
    const photos = byStage.get(stage);
    if (!photos || photos.length === 0) continue;
    photos.sort(
      (a, b) =>
        a.display_order - b.display_order ||
        (a.created_at < b.created_at
          ? -1
          : a.created_at > b.created_at
            ? 1
            : 0),
    );
    sections.push({ stage, label, photos, startIndex });
    startIndex += photos.length;
  }

  return { sections, flat: sections.flatMap((s) => s.photos) };
}

/**
 * Flatten submissions → image items that carry a loadable http(s) URL. A row
 * whose host later 500s still renders (the §I7 chain handles it); rows with no
 * usable URL are excluded so the gallery shows real evidence. Soft-deleted rows
 * are excluded here as a defense-in-depth guard — neither the by-entity nor the
 * rollup service filters `deleted_at` on the embedded `media_items(*)`, so a
 * tombstoned photo that retained a `public_url` would otherwise surface as
 * live evidence.
 */
export function imageItemsFromSubmissions(
  submissions: MediaSubmission[] | undefined,
): MediaItem[] {
  if (!submissions) return [];
  return submissions
    .flatMap((s) => s.items ?? [])
    .filter(
      (item) =>
        item.deleted_at == null &&
        item.content_type.startsWith("image/") &&
        safeHttpUrl(item.public_url) != null,
    );
}
